import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { useModal } from 'webapp/components/common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextField,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Checklist } from '@core/entities/checklist/Checklist'
import { useRequestChecklistChanges } from '@core/react-query/features/checklists/checklist'
import { ModalDialogContainer } from '@common/modal'
import { useToast } from '@core/toast'
import { ErrorMessage } from 'components/Messages'

interface ComponentType {
  checklist: Checklist
}

export const RequestChecklistChangesModal: React.FC<ComponentType> = ({
  checklist,
}) => {
  const requestChecklistChanges = useRequestChecklistChanges()
  const [requestChangeValue, setRequestChangeValue] = useState<string>(null)
  const [error, setError] = useState('')
  const { hideModal } = useModal()
  const toast = useToast()

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.requestChanges}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}
      >
        <TextField
          data-test='text-field'
          label={localizedStrings.enterRequestChanges}
          multiline
          maxRows={4}
          value={requestChangeValue}
          onChange={(e) => setRequestChangeValue(e.target.value)}
          sx={{ mt: 1 }}
        />

        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button
          variant='outlined'
          key='cancel-button'
          onClick={hideModal}
          sx={{ mr: 2 }}
        >
          {localizedStrings.cancel}
        </Button>
        <Button
          data-test='confirm-button'
          variant='contained'
          key='request-changes-button'
          disabled={requestChecklistChanges.isLoading || !requestChangeValue}
          onClick={() => {
            requestChecklistChanges.mutate(
              {
                checklistId: checklist.id,
                comment: requestChangeValue,
                approvalId: checklist.approvalReviews?.nextApprovalId,
              },
              {
                onSuccess: () => {
                  hideModal()
                  setRequestChangeValue(null)
                  toast.success(localizedStrings.checklistChangesRequested)
                },
                onError: (e) => setError(e.message),
              },
            )
          }}
        >
          {localizedStrings.requestChanges}
        </Button>
      </DialogActions>
    </ModalDialogContainer>
  )
}
